import { useTranslation } from 'react-i18next';
import { Chip, useThemeColors, ColorsV2, Text, Tooltip } from '@electricjs/arc';
import {
  AssetsMdmStatus,
  AssetStatus,
  EntitlementSlugEnum,
} from '@electricjs/core_entity-client';
import { skipToken } from '@reduxjs/toolkit/query';
import { getOrganizationPackageHasEntitlement } from '@/helpers/getOrganizationPackageHasEntitlement';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationApplicationByOrganizationApplicationIdQuery } from '@/redux/slices/organizationApiSlice';
import { Asset } from '@/types/assets';
import { OrganizationPackageWithEntitlements } from '@/types/packages';
import { useFlags } from 'launchdarkly-react-client-sdk';

type MDMStatusChipProps = {
  device: Asset;
  isMultiPlanOrganization: boolean;
  organizationPackages: OrganizationPackageWithEntitlements[] | undefined;
  /** The assignee's package; undefined when unassigned or not visible to the user. */
  assigneePackageId: string | undefined;
};

export const MDMStatusChip = ({
  device,
  isMultiPlanOrganization,
  organizationPackages,
  assigneePackageId,
}: MDMStatusChipProps) => {
  const { t } = useTranslation('pages');
  const assetMdmStatus = device.mdmStatusOverride ?? device.device?.mdmStatus;
  const organizationId = useGetOrganizationId();
  const mdmProviderId = device.device?.mdmProviderId;

  const { data: organizationApplication } =
    useGetOrganizationApplicationByOrganizationApplicationIdQuery(
      mdmProviderId
        ? { organizationApplicationId: mdmProviderId, organizationId }
        : skipToken
    );

  const managedLabel = organizationApplication
    ? t('hardware.deviceManagement.table.status.managedBy', {
        applicationName: organizationApplication.application.name,
      })
    : t('hardware.deviceManagement.table.status.managed');

  const managedTooltip = organizationApplication
    ? t('hardware.deviceManagement.table.mdmStatus.tooltip.managedBy', {
        applicationName: organizationApplication.application.name,
      })
    : t('hardware.deviceManagement.table.mdmStatus.tooltip.managed');

  const { ithDemoDeviceMdmNumbers, ithEnableNotApplicableMdmFlagForDevices } =
    useFlags();
  const organizationHasMDMEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const employeeHasPackageWithMdmEntitlement =
    getOrganizationPackageHasEntitlement(
      organizationPackages,
      assigneePackageId ?? '',
      EntitlementSlugEnum.JumpcloudMdm
    );

  const deviceHasValidAssetStatus =
    device.status === AssetStatus.Available ||
    device.status === AssetStatus.InUse;

  const canViewDeviceMdmStatus = isMultiPlanOrganization
    ? employeeHasPackageWithMdmEntitlement
    : organizationHasMDMEntitlement;

  const isManagedExternally = !!mdmProviderId;

  const [managedColorBg, managedColorText] = useThemeColors([
    ColorsV2.SUCCESS_LIGHTEST,
    ColorsV2.SUCCESS_DARK,
  ]);

  const [nonReportingColorBg, nonReportingColorText] = useThemeColors([
    ColorsV2.WARNING_LIGHTEST,
    ColorsV2.WARNING_DARKEST,
  ]);

  const [unmanagedColorBg, unmanagedColorText] = useThemeColors([
    ColorsV2.ERROR_LIGHTEST,
    ColorsV2.ERROR_DARKEST,
  ]);

  const [unknownColorBg, unknownColorText] = useThemeColors([
    ColorsV2.GRAY_LIGHTER,
    ColorsV2.GRAY_DARKEST,
  ]);

  if (ithDemoDeviceMdmNumbers) {
    return (
      <Tooltip
        content={managedTooltip}
        display="flex"
        maxWidth="25rem"
        placement="top">
        <Chip
          icon="shield-check"
          variant="small"
          id="mdm-status-managed-chip"
          backgroundColor={managedColorBg}
          textColor={managedColorText}>
          <Text
            variant="legal"
            color={managedColorText}
            maxWidth="14rem"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap">
            {managedLabel}
          </Text>
        </Chip>
      </Tooltip>
    );
  }

  // Device explicitly exempted from MDM — only show Not applicable chip when flag is enabled.
  // When the flag is off, NOT_APPLICABLE falls through to the existing logic (shows as "Unmanaged").
  if (
    !isManagedExternally &&
    ithEnableNotApplicableMdmFlagForDevices &&
    device.mdmStatusOverride === AssetsMdmStatus.NotApplicable
  ) {
    return (
      <Tooltip
        content={t(
          'hardware.deviceManagement.table.mdmStatus.tooltip.notApplicable'
        )}
        display="flex"
        maxWidth="25rem"
        placement="top">
        <Chip
          variant="small"
          id="mdm-status-not-applicable-chip"
          backgroundColor={unknownColorBg}
          textColor={unknownColorText}>
          {t('hardware.deviceManagement.table.status.notApplicable')}
        </Chip>
      </Tooltip>
    );
  }

  const showNotApplicable =
    !isManagedExternally &&
    (!canViewDeviceMdmStatus ||
      !device.assignedToEmployee ||
      !deviceHasValidAssetStatus ||
      (device.device !== null && assetMdmStatus === undefined));

  if (showNotApplicable) {
    return (
      <Tooltip
        content={t(
          'hardware.deviceManagement.table.mdmStatus.tooltip.notApplicable'
        )}
        display="flex"
        maxWidth="25rem"
        placement="top">
        <Chip
          variant="small"
          id="mdm-status-not-applicable-chip"
          backgroundColor={unknownColorBg}
          textColor={unknownColorText}>
          {t('hardware.deviceManagement.table.status.notApplicable')}
        </Chip>
      </Tooltip>
    );
  }

  if (!isManagedExternally && assetMdmStatus === AssetsMdmStatus.NonReporting) {
    return (
      <Tooltip
        content={t(
          'hardware.deviceManagement.table.mdmStatus.tooltip.nonReporting'
        )}
        display="flex"
        maxWidth="25rem"
        placement="top">
        <Chip
          variant="small"
          icon="alert-triangle"
          id="mdm-status-non-reporting-chip"
          backgroundColor={nonReportingColorBg}
          textColor={nonReportingColorText}>
          {t('hardware.deviceManagement.table.status.nonReporting')}
        </Chip>
      </Tooltip>
    );
  }

  // NotManaged, no device association, or any status other than Managed (e.g. manually created asset) → Unmanaged
  if (!isManagedExternally && assetMdmStatus !== AssetsMdmStatus.Managed) {
    return (
      <Tooltip
        content={t(
          'hardware.deviceManagement.table.mdmStatus.tooltip.unmanaged'
        )}
        display="flex"
        maxWidth="25rem"
        placement="top">
        <Chip
          variant="small"
          id="mdm-status-unmanaged-chip"
          icon="alert-circle"
          backgroundColor={unmanagedColorBg}
          textColor={unmanagedColorText}>
          {t('hardware.deviceManagement.table.status.unmanaged')}
        </Chip>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={managedTooltip}
      display="flex"
      maxWidth="25rem"
      placement="top">
      <Chip
        icon="shield-check"
        variant="small"
        id="mdm-status-managed-chip"
        backgroundColor={managedColorBg}
        textColor={managedColorText}>
        <Text
          variant="legal"
          color={managedColorText}
          maxWidth="14rem"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap">
          {managedLabel}
        </Text>
      </Chip>
    </Tooltip>
  );
};
